import * as React from 'react'
import styled from 'styled-components'
import { SPACE } from '../../../styles/constants'
import { calcFontScale } from '../../../styles/calcFontScale'
import { getBreakpoint } from '../../../styles/getBreakpoint'
import { isBreakpoint } from '../../../styles/isBreakpoint'
import { generateDelay } from '../../../styles/generateDelay'
import { unfurl, fadeIn } from '../../../styles/keyframes'
// @ts-ignore
import * as theme from 'styled-theming'

import { Icon } from '../../Icon'
import { Overlay } from '../../Overlay'
import { NoBackgroundScroll } from '../../NoBackgroundScroll'

interface Props {
  options: Option<string>[]
  onClose: () => void
  onChange: (option: Option<string>) => void
  parentElem: Element
  title?: string | JSX.Element
  selected?: Option<string>
}

interface State {
  offsetTop?: number
  offsetLeft?: number
  windowWidth?: number
  windowHeight?: number
}

@NoBackgroundScroll
export class SelectMenu extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.updatePosition = this.updatePosition.bind(this)
    this.state = { offsetTop: 0, offsetLeft: 0 }
  }

  element?: HTMLDivElement

  componentDidMount() {
    this.updatePosition()
    window.addEventListener('resize', this.updatePosition)
    document.addEventListener('click', this.handleClick, false)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updatePosition)
    document.removeEventListener('click', this.handleClick, false)
  }

  updatePosition() {
    this.setState({
      offsetTop: this.calcOffsetTop(),
      offsetLeft: this.calcOffsetLeft(),
    })
  }

  calcOffsetTop = () => {
    const button = this.props.parentElem
    const menu = this.element
    if (menu) {
      const buttonOffsetTop = button.getBoundingClientRect().top
      const buttonOffsetBottom = buttonOffsetTop + button.clientHeight
      const availableSpace = window.innerHeight - buttonOffsetBottom
      if (availableSpace >= menu.clientHeight) {
        return buttonOffsetBottom + 8
      }
      return buttonOffsetTop - menu.clientHeight - 8
    }
  }

  calcOffsetLeft = () => {
    const button = this.props.parentElem
    const menu = this.element
    if (menu) {
      const buttonOffsetLeft = button.getBoundingClientRect().left
      const availableSpace = window.innerWidth - buttonOffsetLeft
      if (availableSpace >= menu.clientWidth) {
        return buttonOffsetLeft
      }
      return buttonOffsetLeft - (menu.clientWidth - button.clientWidth)
    }
  }

  calcPosition = () => {
    if (isBreakpoint(1)) {
      const { offsetTop, offsetLeft } = this.state
      return {
        top: `${offsetTop}px`,
        left: `${offsetLeft}px`,
      }
    }
  }

  handleClick = (event: MouseEvent) => {
    if (this.element && !this.element.contains(event.target as Node)) {
      this.props.onClose()
    }
  }

  render() {
    const { title, options, onClose, onChange, selected } = this.props
    return (
      <Wrapper>
        <Overlay />
        <Inner
          innerRef={(element) => (this.element = element)}
          role="menu"
          style={this.calcPosition()}
        >
          <Header>
            <CloseButton onClick={onClose}>
              <Icon icon="cancel" size={20} />
            </CloseButton>
          </Header>
          {title && <Title>{title}</Title>}
          <Items>
            {options.map((option: Option<string>) => (
              <Item role="menuitem" key={option.value}>
                <ItemLink
                  role="option"
                  onClick={() => {
                    onChange(option)
                  }}
                >
                  {option.label}
                  {selected.value === option.value && <CheckIcon size={20} icon="verified" />}
                </ItemLink>
              </Item>
            ))}
          </Items>
        </Inner>
      </Wrapper>
    )
  }
}

const colors = {
  background: theme('mode', {
    light: 'white',
    dark: 'rgb(20, 20, 20)',
  }),
  border: theme('mode', {
    light: '#ddd',
    dark: 'rgb(47, 47, 47)',
  }),
}

const Wrapper = styled.aside``
const Inner = styled.div`
  background-color: ${colors.background};
  border-width: 1px;
  border-style: solid;
  border-color: ${colors.border};
  box-sizing: border-box;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  ${getBreakpoint(1)`
		border-radius: 2px; 
		width: auto;
		height: auto; 
		bottom: auto;
		right: auto; 
	`};
`
const Header = styled.header`
  align-items: center;
  box-sizing: border-box;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  width: 100%;
  ${SPACE.inset.s};
  ${getBreakpoint(1)`
		display: none;
	`};
`
const CloseButton = styled.button`
  background-color: transparent;
  border: 0;
  cursor: pointer;
  line-height: 0;
  margin: 0;
  padding: 0;
`
const Title = styled.h2`
  ${calcFontScale(1)};
  ${SPACE.inset.s};
  ${getBreakpoint(1)`
		${calcFontScale(0.25)};
		${SPACE.inset.squish.s};
		border-bottom: 1px solid ${colors.border};
	`};
`

const Items = styled.ul`
  margin: 0;
  padding: 0;
  animation: ${unfurl} 300ms ease-out forwards;
  transform-origin: 0 0;
`
const Item = styled.li`
  opacity: 0;
  animation: ${fadeIn} 300ms ease-out forwards;
  ${generateDelay('&', 3, 0.3)} position: relative;
  list-style-type: none;
`
const ItemLink = styled.a`
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: opacity 150ms ease-in-out;
  ${calcFontScale(0)};
  ${SPACE.inset.s};
  ${getBreakpoint(1)`
		${SPACE.inset.squish.s};
	`} &:hover {
    opacity: 0.65;
  }
`

const CheckIcon = styled(Icon)`
  opacity: 0.65;
  ${SPACE.inline.xs};
`



// WEBPACK FOOTER //
// ./src/components/Select/SelectMenu/index.tsx